import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router";
import { isFeatureEnabled } from "@shared-core/config/features";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { paths } from "@shared-core/routing/routes";
import { useSmartBack } from "../../app/useSmartBack";
import type { EntityOption } from "../../app/components/patterns/EntityCombobox";
import {
  MerchandiseRequestFlow,
  customerLabel,
} from "./MerchandiseRequestFlow";

/**
 * Raise a merchandise request at `/estimates/new`. The CIS estimating tool is
 * retired (non-dictionary work stays in Tharstern), so this is the only thing
 * "new" on the estimates surface: a request to the merchandise team, who quote
 * it in Promo Office. `?customer=CODE` prefills the customer.
 */
export function NewMerchandiseRequestPage() {
  const adapter = useDataAdapter();
  const goBack = useSmartBack(paths.estimates());
  const [searchParams] = useSearchParams();
  const prefilledCustomerCode = searchParams.get("customer") ?? "";
  const [customer, setCustomer] = useState<EntityOption | null>(null);

  useEffect(() => {
    if (!prefilledCustomerCode) return;
    let cancelled = false;
    // The URL carries the Tharstern customer code; the list endpoint's `q`
    // matches code and name server-side, so the combobox shows what was picked.
    adapter
      .getCustomers({ q: prefilledCustomerCode, pageSize: 5 })
      .then((r) => {
        if (cancelled) return;
        const match =
          r.items.find((c) => c.customer_code === prefilledCustomerCode) ??
          r.items[0];
        const code = match?.customer_code || prefilledCustomerCode;
        setCustomer({
          id: code,
          label: match ? customerLabel(code, match.name) : code,
          hint: match?.name,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setCustomer({
            id: prefilledCustomerCode,
            label: prefilledCustomerCode,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, prefilledCustomerCode]);

  if (!isFeatureEnabled("merchandise_draft_estimates")) {
    return <Navigate to={paths.estimates()} replace />;
  }

  return (
    <MerchandiseRequestFlow
      initialCustomer={customer}
      onBackToEstimates={goBack}
    />
  );
}
